import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronUp, Plus, Eye, Trash2, Loader2, X, Upload } from 'lucide-react';
import _ from 'lodash';

import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { useToast } from "../components/ui/Toast";

export const RagList: React.FC = () => {
  const initialLoadRef = useRef(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [displayedDocuments, setDisplayedDocuments] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[] | null>(null);
  
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const { addToast } = useToast();
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 20;

  const updateDisplayedDocuments = useCallback((currentPage: number, docsArray: any[]) => {
    const startIndex = 0;
    const endIndex = currentPage * ITEMS_PER_PAGE;
    setDisplayedDocuments(docsArray.slice(startIndex, endIndex));
    setPage(currentPage);
  }, []);

  const loadMoreDocuments = useCallback(() => {
    if (!searchTerm && !loading) {
      const nextPage = page + 1;
      updateDisplayedDocuments(nextPage, documents);
    }
  }, [searchTerm, loading, page, documents, updateDisplayedDocuments]);

  const fetchDocuments = useCallback(async () => {
    if (loading || initialLoadRef.current) return;
    
    setLoading(true);
    try {
      initialLoadRef.current = true;
      // TODO: Replace with actual API call to fetch RAG documents
      // const fetchedDocs = await ragApi.getDocuments();
      const fetchedDocs: any[] = []; // Mock data for now
      setDocuments(fetchedDocs);
      setTotalCount(fetchedDocs.length);
      updateDisplayedDocuments(1, fetchedDocs);
    } catch (error) {
      console.error('Error fetching documents:', error);
      addToast({
        title: "Error",
        description: "Failed to retrieve documents. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [loading, updateDisplayedDocuments, addToast]);

  const handleSearch = _.debounce((term: string) => {
    setSearchTerm(term);
    if (term) {
      const results = documents.filter(doc =>
        doc.name?.toLowerCase().includes(term.toLowerCase()) ||
        doc.description?.toLowerCase().includes(term.toLowerCase()) ||
        doc.tags?.some((tag: string) => tag.toLowerCase().includes(term.toLowerCase()))
      );
      setSearchResults(results);
    } else {
      setSearchResults(null);
      updateDisplayedDocuments(1, documents);
    }
  }, 300);

  const confirmDelete = (id: string) => {
    setDocToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (docToDelete) {
      setIsDeletingId(docToDelete);
      try {
        // TODO: Replace with actual API call to delete document
        // await ragApi.deleteDocument(docToDelete);
        addToast({
          title: "Success",
          description: "Document successfully deleted",
          variant: "default",
        });
        initialLoadRef.current = false;
        fetchDocuments();
      } catch (error) {
        addToast({
          title: "Error",
          description: "Failed to delete document. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsDeletingId(null);
        setDeleteDialogOpen(false);
        setDocToDelete(null);
      }
    }
  };

  const handleUploadDocument = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // TODO: Replace with actual API call to upload document
      // const formData = new FormData();
      // formData.append('file', file);
      // await ragApi.uploadDocument(formData);
      
      addToast({
        title: "Success",
        description: "Document uploaded successfully",
        variant: "default",
      });
      
      // Refresh the documents list
      initialLoadRef.current = false;
      await fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      addToast({
        title: "Error",
        description: "Failed to upload document. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      // Reset the file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    const handleScroll = _.throttle(() => {
      setShowScrollTop(window.scrollY > 400);

      if (!searchTerm && !loading) {
        const scrolledToBottom =
          window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 800;

        if (scrolledToBottom) {
          loadMoreDocuments();
        }
      }
    }, 200);

    if (initialLoadRef.current) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [searchTerm, loading, loadMoreDocuments]);

  const currentDocuments = searchResults || displayedDocuments;

  return (
    <div className="container mx-auto p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Document Management
        </h1>
        <p className="text-sm font-medium text-gray-500 text-muted-foreground mt-1 mb-6">
          Upload and manage documents for Retrieval-Augmented Generation (RAG).
        </p>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="w-full sm:max-w-md">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search documents by name or description..."
                className="w-full pl-10 pr-8 py-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  handleSearch(e.target.value);
                }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    handleSearch('');
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="w-full sm:w-auto flex gap-2">
            <input
              type="file"
              id="document-upload"
              className="hidden"
              accept=".pdf,.txt,.md,.docx"
              onChange={handleUploadDocument}
              disabled={isUploading}
            />
            <label
              htmlFor="document-upload"
              className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 ${
                isUploading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
              }`}
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Document
                </>
              )}
            </label>
          </div>
        </div>
      </div>

      {loading && !initialLoadRef.current ? (
        <div className="flex justify-center items-center h-64">
          <Spinner className="h-8 w-8 text-blue-600" />
        </div>
      ) : currentDocuments.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            No documents found
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Get started by uploading a new document.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {currentDocuments.map((doc) => (
            <Card key={doc.id} className="overflow-hidden hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium text-gray-900 dark:text-white truncate">
                    {doc.name || 'Untitled Document'}
                  </h3>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => {}}
                      className="text-gray-400 hover:text-blue-600 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="View details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => confirmDelete(doc.id)}
                      disabled={isDeletingId === doc.id}
                      className="text-gray-400 hover:text-red-600 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                      title="Delete document"
                    >
                      {isDeletingId === doc.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                {doc.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
                    {doc.description}
                  </p>
                )}
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>{doc.uploadDate || 'Unknown date'}</span>
                  <span>{doc.size || 'N/A'}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 p-2 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors"
          aria-label="Scroll to top"
        >
          <ChevronUp className="h-6 w-6" />
        </button>
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirmed}
        title="Delete Document"
        description="Are you sure you want to delete this document? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default RagList;
